/**
 * Next Story Bags — product card secondary-image preloader.
 *
 * The hover (lifestyle) image ships with its srcset parked in data-* so it
 * never competes with above-the-fold primaries. When a card comes within
 * ~300px of the viewport we promote the sources, wait for the browser to
 * fully decode the bitmap, then flag the container `.is-ready` — only then
 * does the CSS cross-fade engage, so the first hover never flickers.
 *
 * Touch-only devices have no hover, so we skip the extra bytes entirely.
 * Cards injected later (facet filtering, recommendations, quick-add) are
 * picked up by a MutationObserver.
 */
(() => {
  const SELECTOR = '[data-nsb-card-media]:not([data-nsb-bound])';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const promote = (container) => {
    const img = container.querySelector('.nsb-card-media__img--secondary');
    if (!img || container.classList.contains('is-ready')) return;

    const markReady = () => container.classList.add('is-ready');

    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');

    if (typeof img.decode === 'function') {
      img.decode().then(markReady).catch(() => {
        // decode() can reject for cached/complete images in some engines
        img.complete && img.naturalWidth > 1 ? markReady() : img.addEventListener('load', markReady, { once: true });
      });
    } else {
      img.complete ? markReady() : img.addEventListener('load', markReady, { once: true });
    }
  };

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              io.unobserve(entry.target);
              promote(entry.target);
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  const bind = (root = document) => {
    if (!canHover.matches) return;
    root.querySelectorAll(SELECTOR).forEach((el) => {
      el.setAttribute('data-nsb-bound', '');
      io ? io.observe(el) : promote(el);
    });
  };

  const start = () => {
    bind();

    // If a mouse is attached later (e.g. tablet + trackpad), bind then.
    canHover.addEventListener?.('change', () => bind());

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(SELECTOR)) bind(node.parentElement || document);
          else if (node.querySelector?.(SELECTOR)) bind(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
